import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase, readableError } from '../../lib/supabase'
import { useSession } from '../../lib/session'
import { usePlatform } from '../../lib/platform'
import { Shell } from '../../components/Shell'
import { Button, Spinner } from '../../components/ui'
import { useSongDelete, useRecentlyDeleted, daysLeft } from '../../lib/songDelete'
import SwipeRow from '../../components/SwipeRow'

/** A mouse has no swipe gesture to reveal SwipeRow's action, so desktop gets
 *  a plain, always-visible delete button instead of a hidden one nothing can
 *  trigger. */
function TrashIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 7h16" />
      <path d="M9 7V4h6v3" />
      <path d="M6 7l1 13a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-13" />
      <path d="M10 11v6M14 11v6" />
    </svg>
  )
}

/**
 * Drafts, on their own screen.
 *
 * They used to sit at the top of the profile, which meant the first thing a
 * writer saw about themselves was a pile of unfinished work. A draft is a
 * private workspace, not part of how you present yourself — so the profile
 * counts them and this screen holds them.
 *
 * Deleting goes through archive_delete_song(), not a raw delete. removed_at is
 * still not the mechanism — that is moderation, a song taken down by someone
 * else, and reusing it for "I threw this away" would leave abandoned drafts in
 * the moderation tables forever. Instead the row is copied into deleted_songs
 * for thirty days and then really deleted, which is what makes "Recently
 * deleted" below possible and, more importantly, what stops the recording
 * being orphaned in R2: the purge finds the file through that archive's media
 * queue, where the old delete left nothing pointing at it at all.
 *
 * The function refuses a submitted song in the same terms the RLS policy did,
 * so a sent song still can't be erased from a church's queue.
 */

/** How long the recording on this draft has left. */
function mediaClock(song) {
  if (!song.r2_object_key || !song.media_expires_at) return null
  const mins = Math.round((new Date(song.media_expires_at) - Date.now()) / 60000)
  if (mins <= 0) return 'recording expiring now'
  if (mins < 60) return `recording goes in ${mins} min`
  return 'recording goes in an hour'
}

export default function Drafts() {
  const { user } = useSession()
  const { device } = usePlatform()
  const isPhone = device === 'phone'
  const nav = useNavigate()
  const [drafts, setDrafts] = useState(null)
  const [err, setErr] = useState(null)

  const load = useCallback(async () => {
    const { data, error } = await supabase
      .from('songs')
      .select('*, song_cowriters(id, split_percentage, signed)')
      .eq('songwriter_id', user.id)
      .is('submitted_at', null)
      .is('finished_at', null)
      .order('created_at', { ascending: false })
    if (error) return setErr(readableError(error))
    setDrafts(data ?? [])
  }, [user.id])

  useEffect(() => { load() }, [load])

  const bin = useRecentlyDeleted()
  const { ask, dialog } = useSongDelete(() => { load(); bin.reload() })

  if (drafts === null) return <Shell back="/me" title="Profile"><Spinner /></Shell>

  return (
    <Shell back="/me" title="Profile" tabs>
      <div className="eyebrow">Drafts</div>
      <h2 className="q">Still being written.</h2>
      <p className="sub">
        Nobody sees these but you.
        {isPhone ? ' Swipe a draft to delete it.' : ' Use the trash icon to delete one.'}
      </p>

      {drafts.length === 0 ? (
        <div className="empty" style={{ marginTop: 20 }}>
          <p className="sub" style={{ marginBottom: 6 }}>Nothing on the bench.</p>
          <p className="small">
            Drafts land here the moment you start one, and stay until you send
            them or throw them away.
          </p>
          <button className="btn btn-primary" style={{ marginTop: 16 }}
                  onClick={() => nav('/songs/new')}>Start one</button>
        </div>
      ) : (
        <div style={{ marginTop: 14 }}>
          {drafts.map((s) => {
            const total = s.song_cowriters?.reduce((a, c) => a + Number(c.split_percentage), 0) ?? 0
            const signed = s.song_cowriters?.filter((c) => c.signed).length ?? 0
            const count = s.song_cowriters?.length ?? 0
            const clock = mediaClock(s)
            const row = (
              <button className="action col" onClick={() => nav(`/songs/${s.id}/1`)}>
                <div className="row">
                  <span>{s.title || 'Untitled'}</span>
                  <span className="ar">›</span>
                </div>
                <small>
                  Started {new Date(s.created_at).toLocaleDateString()}
                  {count > 0 && ` · ${signed}/${count} signed · splits ${total}%`}
                </small>
                {clock && <small className="clock">⏱ {clock}</small>}
              </button>
            )

            // Swipe is a phone gesture — a mouse has nothing that triggers it,
            // so desktop gets a real, always-clickable delete button instead
            // of an action hidden behind a gesture that can't happen here.
            return isPhone ? (
              <SwipeRow key={s.id} actionLabel="Delete" onAction={() => ask(s)}>
                {row}
              </SwipeRow>
            ) : (
              <div key={s.id} style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
                <div style={{ flex: 1 }}>{row}</div>
                <button className="iconbtn icon-only" style={{ height: 'auto' }}
                        aria-label={`Delete "${s.title || 'Untitled'}"`}
                        onClick={() => ask(s)}>
                  <TrashIcon />
                </button>
              </div>
            )
          })}
        </div>
      )}

      {err && <p className="err">{err}</p>}

      {/* What you deleted and haven't lost yet. Deleting is immediate — this
          is not a bin you have to empty, it is thirty days of being allowed to
          change your mind, and then it goes on its own. */}
      {bin.rows.length > 0 && (
        <>
          <div className="hr" style={{ marginTop: 26 }} />
          <div className="eyebrow">Recently deleted</div>
          <p className="small" style={{ marginTop: 4 }}>
            Songs you deleted, and clips that ran out their 24 hours. Kept for
            thirty days, then gone for good.
          </p>
          {bin.err && <p className="err">{bin.err}</p>}
          <div style={{ marginTop: 10 }}>
            {bin.rows.map((r) => (
              <div key={r.song_id}
                   style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <div className="col" style={{ flex: 1, opacity: .75 }}>
                  <span>{r.title || 'Untitled'}</span>
                  <small>
                    {r.reason === 'unfinished clip expired'
                      ? 'Clip expired'
                      : `Deleted ${new Date(r.deleted_at).toLocaleDateString()}`}
                    {' · '}{daysLeft(r.purge_after)}
                  </small>
                </div>
                <Button className="btn btn-quiet" busy={bin.busyId === r.song_id}
                        onClick={() => bin.restore(r).then((ok) => ok && load())}>
                  Put it back
                </Button>
              </div>
            ))}
          </div>
        </>
      )}

      {dialog}
    </Shell>
  )
}
